import { cineamoApiClient } from '../../apiClient';
import { UrlQueryParameter, buildUrlQuery } from '../../helpers/url-query-helper';
import { CinemaDto } from '../../models/cinema/CinemaDto.types';
import { CinemaRoomDto } from '../../models/cinema-room/CinemaRoomDto.types';
import { MovieDto } from '../../models/movie/v2/MovieDto.types';
import { ShowingDto, ShowingInputDto } from '../../models/showing/ShowingDto.types';
import { ApiErrorResponse } from '../../types/ApiErrorResponse.types';
import { ShowingListResponse, ShowingTagListResponse } from '../../types/ApiListResponse.types';
import { ApiResponse } from '../../types/ApiResponse.types';

import { ShowingApiEmbed } from './showing-api.embed';
import { ShowingApiGroup } from './showing-api.group';

// <------------------------ Showings ------------------------>

// Map embed enum to ShowingDto embed keys and types
type EmbedMap = {
    [ShowingApiEmbed.Cinema]: { cinema: CinemaDto };
    [ShowingApiEmbed.CinemaRoom]: { cinemaRoom: CinemaRoomDto };
};

/** The embeds a `getShowings` caller actually asked for, resolved from its `embed` array. */
type RequestedShowingEmbeds<Q extends GetShowingsQuery> = NonNullable<ShowingDto['_embedded']> &
    Pick<EmbedMap, Extract<NonNullable<Q['embed']>[number], keyof EmbedMap>>;

export type GetShowingsQuery = {
    contentId?: string | number;
    cinemas?: number[] | string[];
    cinemaChainIds?: number[];
    startDatetime?: string;
    endDatetime?: string;
    /** Filter by movie id (Content.movie). Takes precedence over `cineamoMovieId`. */
    movieId?: MovieDto['id'];
    /**
     * @deprecated Prefer `movieId`. Retained only for the embedded showtimes-finder widget, whose
     * `?movie=` param is set by third-party sites we do not control and can still carry a legacy
     * `…m` id — see `buildShowingsMovieFilter` (CIN-13072).
     */
    cineamoMovieId?: MovieDto['cineamoId'];
    name?: string;
    longitude?: number;
    latitude?: number;
    distance?: number;
    isRegularShowtime?: boolean;
    isEventRequest?: boolean;
    isThreeDimensional?: boolean;
    isImax?: boolean;
    isDolbyAtmos?: boolean;
    isDolbyVision?: boolean;
    isDolbyCinema?: boolean;
    is4DX?: boolean;
    isScreenX?: boolean;
    isHFR?: boolean;
    isLive?: boolean;
    isDbox?: boolean;
    isInfinityVision?: boolean;
    isOriginalLanguage?: boolean;
    isSubtitled?: boolean;
    hasMovie?: boolean;
    group?: ShowingApiGroup;
    embed?: ShowingApiEmbed[];
} & UrlQueryParameter;

/**
 * This endpoint returns a list of showings.
 * Return type is automatically inferred from embed parameter.
 *
 * @example
 * // Without embeds - all optional
 * getShowings({ cinemas: [1] });
 *
 * // With cinema embed - cinema required in return type
 * getShowings({ embed: [ShowingApiEmbed.Cinema] });
 *
 * @param query
 * @returns {ApiResponse<ShowingListResponse>} with typed ShowingDto
 */
export async function getShowings<Q extends GetShowingsQuery>(
    query: Q
): Promise<ApiResponse<unknown, ShowingListResponse<RequestedShowingEmbeds<Q>>> | null> {
    return cineamoApiClient
        .get<ApiResponse<unknown, ShowingListResponse<RequestedShowingEmbeds<Q>>>>(`/showings${buildUrlQuery(query)}`)
        .then((response) => response.data)
        .catch(() => {
            return null;
        });
}

// <------------------------ Showing ------------------------>

/**
 * This endpoint returns a single showing.
 *
 * @example
 * // Get a specific showing by content and request id:
 * getShowingByRequestId();
 *
 * @param showingId
 * @returns {ApiResponse<ShowingDto>}
 */

export async function getShowingById(showingId: number | string): Promise<ApiResponse<ShowingDto> | null> {
    return cineamoApiClient
        .get<ApiResponse<ShowingDto>>(`/showings/${showingId}`)
        .then((response) => response.data)
        .catch(() => {
            return null;
        });
}

export async function createShowing(
    showing: ShowingInputDto
): Promise<ApiResponse<ShowingDto> | ApiErrorResponse<ShowingInputDto>> {
    return cineamoApiClient
        .post('/showings', showing)
        .then((response) => response.data)
        .catch((error) => {
            return error;
        });
}

export async function updateShowing(
    showing: ShowingInputDto
): Promise<ApiResponse<ShowingDto> | ApiErrorResponse<ShowingInputDto>> {
    return cineamoApiClient
        .patch(`/showings/${showing.id}`, showing)
        .then((response) => response.data)
        .catch((error) => {
            return error;
        });
}

export async function deleteShowing(
    showing: ShowingInputDto
): Promise<ApiResponse<ShowingDto> | ApiErrorResponse<ShowingInputDto>> {
    return cineamoApiClient
        .delete(`/showings/${showing.id}`)
        .then((response) => response)
        .catch((error) => {
            return error;
        });
}

export async function addTagToShowing(
    showingId: number,
    showingTagId: number
): Promise<ApiResponse<ShowingDto> | ApiErrorResponse<ShowingInputDto>> {
    return cineamoApiClient
        .put(`/showings/${showingId}/showing-tags/${showingTagId}`)
        .then((response) => response)
        .catch((error) => {
            return error;
        });
}

export async function removeTagFromShowing(showingId: number, showingTagId: number): Promise<boolean> {
    return cineamoApiClient
        .delete(`/showings/${showingId}/showing-tags/${showingTagId}`)
        .then((response) => response.status === 204)
        .catch((error) => {
            return error;
        });
}

// <------------------------ Cinema Future Showings ------------------------>

export type GetCinemaFutureShowingsQuery = {
    showingTagIds?: number[] | string[];
    hasShowingTags?: boolean;
    /** Filter by movie id (Content.movie). */
    movieId?: MovieDto['id'];
    embed?: 'cinemaRoom'[];
} & UrlQueryParameter;

/**
 * Returns all future showings for a cinema.
 * This endpoint includes showingTagIds on each showing (unlike /showings).
 * Backend hardcodes perPage: 5000 and startDatetime: today 04:00.
 */
export async function getCinemaFutureShowings(
    cinemaId: CinemaDto['id'] | string,
    query?: GetCinemaFutureShowingsQuery
): Promise<ApiResponse<unknown, ShowingListResponse> | null> {
    return cineamoApiClient
        .get<ApiResponse<unknown, ShowingListResponse>>(`/cinemas/${cinemaId}/showings-future${buildUrlQuery(query)}`, {
            credentials: 'same-origin'
        })
        .then((response) => response.data)
        .catch(() => {
            return null;
        });
}

// <------------------------ Showing Tags ------------------------>

type GetShowingTagsParams = UrlQueryParameter;
export async function getShowingTags(
    showingId: ShowingDto['id'] | string,
    params?: GetShowingTagsParams
): Promise<ApiResponse<ShowingTagListResponse> | null> {
    return await cineamoApiClient
        .get<ApiResponse<ShowingTagListResponse>>(`/showings/${showingId}/tags${buildUrlQuery(params)}`, {
            credentials: 'same-origin'
        })
        .then((response) => response.data)
        .catch(() => {
            return null;
        });
}
